import { Spinner } from "components";
import { useState } from "react";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { FormField } from "pages/GenericOrder/ui-form/form-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { useMutation, useQuery } from "react-query";
import { z } from "zod";
import { cancelForexOrder } from "../apis";
import { getForexCancelContext } from "service/moneeflo/legacy-forex-order-api";
import { formatInrMinor, inputToMinor, isPositiveMinor } from "pages/GenericOrderDetailsPage/utils/money";
import CancelOrderDialog from "./CancelOrderDialog";
import { useForexAccess } from "pages/GenericOrderDetailsPage/hooks/useForexAccess";

const refundReasons = [
  {
    value: "Incomplete / incorrect documentation",
    label: "Incomplete / incorrect documentation",
  },
  {
    value: "Mismatch in traveler details",
    label: "Mismatch in traveler details",
  },
  {
    value: "Customer not willing to pay TCS",
    label: "Customer not willing to pay TCS",
  },
  { value: "Wrong product selected", label: "Wrong product selected" },
  {
    label: "Change of travel plan / trip cancelled",
    value: "Change of travel plan / trip cancelled",
  },
  {
    label: "Did not like the quality of currency notes",
    value: "Did not like the quality of currency notes",
  },
];

/**
 * The fee as typed, in paise: blank is no fee, "500." is 500 rupees, and
 * anything that is not an amount with at most two decimals is null.
 */
const feeToMinor = (text: string | undefined): bigint | null => {
  const t = (text ?? "").trim().replace(/\.$/, "");
  return t === "" ? 0n : inputToMinor(t);
};

const formSchema = z.object({
  reason: z
    .string()
    .min(1, { message: "Please select a reason for cancellation" }),
  // The fee is kept as typed and turned into paise once; blank or 0 sends none.
  quantity: z
    .string()
    .optional()
    .refine((v) => feeToMinor(v) !== null, { message: "Enter the charge in rupees, with at most two decimals" }),
});

export default function CancelForexOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canDecide, canEditServiceCharge: canCharge, canRefund: mayRefund } = useForexAccess();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<z.infer<typeof formSchema> | null>(null);
  const [pendingRefund, setPendingRefund] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { reason: "", quantity: undefined },
  });

  const { data: ctx, isLoading: contextLoading, error: contextError } = useQuery({
    queryKey: ["cancelContext", id],
    queryFn: getForexCancelContext,
  });

  // A draft or a quote takes no fee, and nor does a person who may not charge one.
  const takesFee = !!ctx && !ctx.unconfirmed && canCharge;
  // What can go back once the cancellation charge is taken off, in paise.
  const feeMinor = takesFee ? feeToMinor(form.watch("quantity")) ?? 0n : 0n;
  const afterFeeMinor = ctx ? (BigInt(ctx.refundableMinor) > feeMinor ? BigInt(ctx.refundableMinor) - feeMinor : 0n).toString() : "0";
  const somethingToRefund = !!ctx && !ctx.unconfirmed && isPositiveMinor(afterFeeMinor);

  const { mutate, isLoading: loading } = useMutation({
    mutationFn: cancelForexOrder,
    onSuccess: (_data, variables) => {
      toast.success("Order cancelled");
      setIsDialogOpen(false);
      setPendingFormData(null);
      if (variables.redirectToRefund) {
        navigate(`/refund/forex/${id}`, {
          state: { cancelReason: variables.reason, refundMinor: variables.refundMinor },
        });
      } else {
        navigate(`/forex/orderdetails/${id}`);
      }
    },
    onError: (error) => {
      toast.error("The order could not be cancelled", {
        description: error instanceof Error ? error.message : undefined,
      });
      setIsDialogOpen(false);
      setPendingFormData(null);
    },
  });

  const send = (values: z.infer<typeof formSchema>, redirectToRefund: boolean) =>
    mutate({
      reason: values.reason,
      // Blank or 0 sends none.
      feeMinor: (takesFee ? feeToMinor(values.quantity) ?? 0n : 0n).toString(),
      order_id: Number(id),
      redirectToRefund,
      refundMinor: afterFeeMinor,
    });

  /** Core refuses a fee larger than the order; say so before asking to confirm. */
  const feeTooLarge = (data: z.infer<typeof formSchema>) => {
    if (!takesFee || !ctx) return false;
    if ((feeToMinor(data.quantity) ?? 0n) <= BigInt(ctx.totalPayableMinor)) return false;
    form.setError("quantity", { message: "The charge cannot be more than the order total" });
    return true;
  };

  const handleFormSubmit = (data: z.infer<typeof formSchema>) => {
    if (feeTooLarge(data)) return;
    setPendingFormData(data);
    setPendingRefund(false);
    setIsDialogOpen(true);
  };

  const handleSubmitAndRefund = form.handleSubmit((data) => {
    if (feeTooLarge(data)) return;
    setPendingFormData(data);
    setPendingRefund(true);
    setIsDialogOpen(true);
  });

  const handleConfirmCancellation = () => {
    if (pendingFormData) send(pendingFormData, pendingRefund);
  };

  if (!canDecide) {
    return (
      <div role="alert" className="m-3 rounded-lg bg-white-A700 p-6 text-sm text-[#333333]">
        <h1 className="mb-2 text-lg font-semibold">You cannot cancel this order</h1>
        <p>Your role does not include cancelling forex orders. Ask a manager if you need this.</p>
      </div>
    );
  }

  return (
    <>
      {(loading || contextLoading) && <Spinner />}
      <CancelOrderDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onConfirm={handleConfirmCancellation}
        isLoading={loading}
      />
      <div className="sm:p-1 flex md:flex-col font-inter items-start justify-between gap-5 mx-auto w-full h-full md:h-auto">
        <section className="gap-5 bg-white-A700 w-full flex h-full md:h-auto flex-col p-3 rounded-lg">
          <div className="flex flex-col gap-5 w-full">
            <div className="flex px-10 md:px-3 relative flex-col gap-5 flex-grow">
              <h1 className="text-lg font-semibold text-[#333333]">
                {ctx ? `Cancel order OD-${ctx.reference}` : "Cancel order"}
              </h1>
              {contextError ? (
                <p role="alert" className="text-sm text-[#7A1F1F]">
                  {contextError instanceof Error ? contextError.message : "The order could not be loaded."}
                </p>
              ) : null}
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6 max-w-md w-full">
                  <div className="space-y-6">
                    <div className="space-y-2">
                      <label htmlFor="cancel-reason" className="text-sm font-medium text-gray-700">
                        Reason for cancellation
                      </label>
                      <FormField
                        form={form}
                        name="reason"
                        render={({ field }) => (
                          <>
                            <Select onValueChange={field.onChange} value={String(field.value ?? "")}>
                              <SelectTrigger id="cancel-reason" className="w-full h-12 px-4 border-gray-300 rounded-lg">
                                <SelectValue placeholder="Select a reason for cancellation" />
                              </SelectTrigger>
                              <SelectContent>
                                {refundReasons.map((reason) => (
                                  <SelectItem key={reason.value} value={reason.value}>
                                    {reason.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {form.formState.errors.reason && (
                              <p role="alert" className="text-sm text-red-700 mt-1">
                                {form.formState.errors.reason.message}
                              </p>
                            )}
                          </>
                        )}
                      />
                    </div>

                    {takesFee && (
                      <div className="space-y-2">
                        <label htmlFor="cancel-fee" className="text-sm font-medium text-gray-700">
                          Charge for cancellation (optional)
                        </label>
                        <FormField
                          form={form}
                          name="quantity"
                          render={({ field }) => (
                            <div className="relative">
                              <span aria-hidden className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-[#555555]">₹</span>
                              <input
                                id="cancel-fee"
                                inputMode="decimal"
                                autoComplete="off"
                                className="h-12 w-full rounded-lg border border-gray-300 pl-8 pr-3 text-sm"
                                value={field.value ?? ""}
                                onBlur={field.onBlur}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d*\.?\d{0,2}$/.test(value)) {
                                    field.onChange(value === "" ? undefined : value);
                                  }
                                }}
                              />
                            </div>
                          )}
                        />
                        {form.formState.errors.quantity && (
                          <p role="alert" className="text-sm text-red-700">
                            {form.formState.errors.quantity.message}
                          </p>
                        )}
                        <p className="text-xs text-[#555555]">Leave blank or 0 to charge nothing.</p>
                      </div>
                    )}

                    {/* What each button does, in a line. */}
                    {ctx && (!somethingToRefund || !mayRefund) ? (
                      <div className="space-y-3">
                        {somethingToRefund ? (
                          <p className="text-sm text-[#333333]">
                            The refund is done later from the order&apos;s Payments tab, by someone who can record payments.
                          </p>
                        ) : (
                          <p data-testid="nothing-to-refund" className="text-sm text-[#333333]">
                            Nothing to refund.
                          </p>
                        )}
                        <button
                          type="submit"
                          className="px-6 py-2.5 text-sm font-medium rounded-lg text-white-A700 bg-blue-700 hover:bg-blue-800 transition-colors cursor-pointer"
                        >
                          Cancel order
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-1">
                          <button
                            type="submit"
                            className="w-fit px-6 py-2.5 text-sm font-medium rounded-lg text-blue-700 border border-blue-700 bg-white hover:bg-blue-50 transition-colors cursor-pointer"
                          >
                            Cancel order
                          </button>
                          <span className="text-xs text-[#555555]">The order is cancelled. The refund is done later from its Payments tab.</span>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            onClick={handleSubmitAndRefund}
                            type="button"
                            disabled={loading}
                            className="w-fit px-6 py-2.5 text-sm font-medium rounded-lg text-white-A700 bg-blue-700 hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                          >
                            Cancel order and refund
                          </button>
                          <span className="text-xs text-[#555555]">The order is cancelled, then the refund page opens with the amount ready.</span>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </Form>
            </div>
          </div>
        </section>

        {/* What core has worked out for this order. */}
        <section className="bg-white-A700 h-full md:h-auto rounded-lg w-1/3 md:w-full p-3">
          <div className="w-full bg-white rounded-xl p-6 shadow-[0px_4px_4px_2px_rgba(239,241,251,1)]">
            <h2 className="text-2xl font-bold mb-6">Calculation</h2>
            <div className="flex flex-col gap-5">
              <ul className="flex flex-col gap-3">
                {(ctx?.lines ?? []).map((line) => (
                  <li key={line.id} className="flex justify-between items-center text-sm text-gray-800">
                    <span>
                      {line.currency} {formatInrMinor(line.fxAmountMinor).replace("₹", "")}
                    </span>
                    <span className="font-medium">{formatInrMinor(line.inrAmountMinor)}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-between items-center px-[15px] py-[17px] bg-[#f2f6ff]">
                <span className="font-semibold text-base">Order total</span>
                <span className="font-medium text-base text-[#333333]">{formatInrMinor(ctx?.totalPayableMinor)}</span>
              </div>
              <div className="flex justify-between items-center px-[15px] text-sm text-[#333333]">
                <span>Received so far</span>
                <span className="font-medium">{formatInrMinor(ctx?.paidMinor)}</span>
              </div>
              {feeMinor > 0n ? (
                <div className="flex justify-between items-center px-[15px] text-sm text-[#B00020]">
                  <span>Cancellation charge</span>
                  <span className="font-medium">{`- ${formatInrMinor(feeMinor)}`}</span>
                </div>
              ) : null}
              <div className="flex justify-between items-center px-[15px] py-[17px] bg-[#f2f6ff] border-t-2 border-[#176bef] rounded-b-xl">
                <span className="font-semibold text-base text-[#176bef]">Can be refunded</span>
                <span data-testid="refundable" className="font-medium text-base text-[#176bef]">
                  {formatInrMinor(afterFeeMinor)}
                </span>
              </div>
              <p className="text-xs text-[#555555]">
                The amount that can go back is worked out by the system from the payments received.
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
